import React, { useState, useEffect } from 'react';
import { matchesAPI } from '../services/api';
import { FaTrophy, FaCalendar, FaFilter, FaSearch, FaUsers } from 'react-icons/fa';
import Footer from '../components/Footer';
import ResultsDisplay from '../components/ResultsDisplay';
import LoadingSpinner from '../components/LoadingSpinner';
import PageCover from '../components/PageCover';
import { useLanguage } from '../context/LanguageContext';

const ResultsPage = () => {
  const { language, t } = useLanguage();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedLeg, setSelectedLeg] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Augmenté pour voir plus de journées

  useEffect(() => {
    loadMatches();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRound, selectedGroup, selectedLeg, searchTerm]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await matchesAPI.getAll();
      const data = response.data || response || [];
      const finishedMatches = Array.isArray(data)
        ? data.filter(m => m && m.status === 'finished')
        : [];
      setMatches(finishedMatches);
    } catch (err) {
      console.error('Erreur ResultsPage:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div style={{ padding: '100px', textAlign: 'center' }}>Error: {error}</div>;

  // Filtrage combiné
  const filteredMatches = matches.filter(m => {
    const matchesRound = selectedRound === 'all' || String(m.round) === String(selectedRound);
    const matchesGroup = selectedGroup === 'all' ||
      String(m.home_team_group) === String(selectedGroup) ||
      String(m.away_team_group) === String(selectedGroup);
    const matchesSearch = searchTerm === '' ||
      (m.home_team_name && m.home_team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (m.away_team_name && m.away_team_name.toLowerCase().includes(searchTerm.toLowerCase()));
      (m.away_team_name && m.away_team_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesLeg = selectedLeg === 'all' || String(m.leg).toLowerCase() === String(selectedLeg).toLowerCase();

    return matchesRound && matchesGroup && matchesSearch && matchesLeg;
  });

  // Extraire les journées et groupes uniques pour les filtres
  const rounds = [...new Set(matches.map(m => m.round))].filter(Boolean).sort((a, b) => a - b);
  const groups = [...new Set(matches.map(m => m.home_team_group))].filter(Boolean).sort((a, b) => a - b);

  // Grouper par journée pour l'affichage final (ORDRE CHRONOLOGIQUE)
  let displayRounds = [...new Set(filteredMatches.map(m => m.round))].filter(Boolean).sort((a, b) => a - b);

  // Logic pour la pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayRounds = displayRounds.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayRounds.length / itemsPerPage);

  const localizedTexts = {
    title: language === 'ar' ? 'نتائج المباريات' : 'Résultats des Matchs',
    subtitle: language === 'ar' ? 'أرشيف كامل لجميع مباريات الموسم 2025/2026' : 'Archive complète de tous les matchs saison 2025/2026'
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  return (
    <div style={styles.container}>
      <PageCover title={localizedTexts.title} subtitle={localizedTexts.subtitle} icon={FaTrophy} />

      <section style={styles.filterSection}>
        <div style={styles.filterCard}>
          <div style={styles.filterHeader}>
            <FaFilter style={styles.filterHeaderIcon} />
            <h3 style={styles.filterHeaderTitle}>{language === 'ar' ? 'تصفية النتائج' : 'Filtrer les résultats'}</h3>
          </div>
          <div style={styles.filterGrid}>
            {/* Recherche par équipe */}
            <div style={styles.filterItem}>
              <label style={styles.label}>{language === 'ar' ? 'البحث عن فريق' : 'Chercher une équipe'}</label>
              <div style={styles.inputWrapper}>
                <FaSearch style={styles.inputIcon} />
                <input
                  type="text"
                  placeholder={language === 'ar' ? 'اسم الفريق...' : 'Nom de l\'équipe...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Filtrage par journée */}
            <div style={styles.filterItem}>
              <label style={styles.label}>{t('round')}</label>
              <div style={styles.inputWrapper}>
                <FaCalendar style={styles.inputIcon} />
                <select
                  value={selectedRound}
                  onChange={(e) => setSelectedRound(e.target.value)}
                  style={styles.select}
                >
                  <option value="all">{language === 'ar' ? 'جميع الجولات' : 'Toutes les journées'}</option>
                  {rounds.map(r => (
                    <option key={r} value={String(r)}>{t('round')} {r}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtrage par groupe */}
            <div style={styles.filterItem}>
              <label style={styles.label}>{language === 'ar' ? 'المجموعة' : 'Groupe'}</label>
              <div style={styles.inputWrapper}>
                <FaUsers style={styles.inputIcon} />
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  style={styles.select}
                >
                  <option value="all">{language === 'ar' ? 'جميع المجموعات' : 'Tous les groupes'}</option>
                  {groups.map(g => (
                    <option key={g} value={String(g)}>{language === 'ar' ? `المجموعة ${g}` : `Groupe ${g}`}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtrage par Phase (Aller/Retour) */}
            <div style={styles.filterItem}>
              <label style={styles.label}>{language === 'ar' ? 'المرحلة' : 'Phase'}</label>
              <div style={styles.inputWrapper}>
                <FaFilter style={styles.inputIcon} />
                <select
                  value={selectedLeg}
                  onChange={(e) => setSelectedLeg(e.target.value)}
                  style={styles.select}
                >
                  <option value="all">{language === 'ar' ? 'جميع الجولات' : 'Toutes les journées'}</option>
                  <option value="aller">{language === 'ar' ? 'ذهاب' : 'Aller'}</option>
                  <option value="retour">{language === 'ar' ? 'إياب' : 'Retour'}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.resultsSection}>
        {currentDisplayRounds.length > 0 ? (
          <div>
            <div style={styles.resultsGrid}>
              {currentDisplayRounds.map(roundNum => (
                <div key={roundNum} style={styles.roundBlock}>
                  <div style={styles.roundHeader}>
                    <FaCalendar style={styles.roundIcon} />
                    <span style={styles.roundTitleText}>{t('round')} {roundNum}</span>
                    <div style={styles.roundBadge}>{filteredMatches.filter(m => String(m.round) === String(roundNum)).length}</div>
                  </div>
                  <ResultsDisplay
                    matches={filteredMatches.filter(m => String(m.round) === String(roundNum))}
                    allMatches={matches}
                    title={`${t('round')} ${roundNum}`}
                    round={roundNum}
                    compact={true}
                  />
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                >
                  {language === 'ar' ? 'السابق' : 'Précédent'}
                </button>

                <div style={styles.pageNumbers}>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      style={{
                        ...styles.pageNumberBtn,
                        ...(currentPage === i + 1 ? styles.pageNumberBtnActive : {})
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
                >
                  {language === 'ar' ? 'التالي' : 'Suivant'}
                </button>
              </div>
            )}

            <div style={styles.paginationInfo}>
              {language === 'ar'
                ? `عرض صفحة ${currentPage} من ${totalPages} (${displayRounds.length} جولة إجمالاً)`
                : `Affichage de la page ${currentPage} sur ${totalPages} (${displayRounds.length} journées au total)`}
            </div>
          </div>
        ) : (
          <div style={styles.noResults}>
            <FaSearch size={50} color="#ccc" style={{ marginBottom: '20px' }} />
            <h3>{language === 'ar' ? 'لا توجد نتائج تطابق بحثك' : 'Aucun résultat ne correspond à votre recherche'}</h3>
            <button onClick={() => { setSelectedRound('all'); setSelectedGroup('all'); setSelectedLeg('all'); setSearchTerm(''); }} style={styles.resetBtn}>
              {language === 'ar' ? 'إعادة ضبط الفلاتر' : 'Réinitialiser les filtres'}
            </button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc' },
  filterSection: { maxWidth: '1200px', margin: '-50px auto 40px', padding: '0 20px', position: 'relative', zIndex: 10 },
  filterCard: { background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid #edf2f7' },
  filterHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' },
  filterHeaderIcon: { color: '#c41e3a', fontSize: '20px' },
  filterHeaderTitle: { margin: 0, fontSize: '18px', fontWeight: '800', color: '#1a365d' },
  filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIcon: { position: 'absolute', left: '15px', color: '#94a3b8' },
  input: { width: '100%', padding: '12px 15px 12px 40px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '15px', outline: 'none', transition: 'all 0.3s' },
  select: { width: '100%', padding: '12px 15px 12px 40px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '15px', background: 'white', appearance: 'none', cursor: 'pointer', outline: 'none' },
  resultsSection: { maxWidth: '1400px', margin: '0 auto', padding: '0 20px 80px' },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '15px',
    marginBottom: '40px'
  },
  roundBlock: { marginBottom: '10px' },
  roundHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '0 5px' },
  roundIcon: { fontSize: '16px', color: '#c41e3a' },
  roundTitleText: { fontSize: '18px', fontWeight: '800', color: '#1e293b' },
  roundBadge: { background: '#c41e3a', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  noResults: { textAlign: 'center', padding: '100px 20px', background: 'white', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', color: '#64748b' },
  resetBtn: { marginTop: '20px', background: '#c41e3a', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '40px', flexWrap: 'wrap' },
  pageNumbers: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' },
  pageBtn: { padding: '10px 20px', background: '#1a365d', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s', fontSize: '14px' },
  pageBtnDisabled: { background: '#cbd5e1', cursor: 'not-allowed' },
  pageNumberBtn: { width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#1a365d', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s' },
  pageNumberBtnActive: { background: '#c41e3a', color: 'white', borderColor: '#c41e3a' },
  paginationInfo: { textAlign: 'center', marginTop: '15px', fontSize: '14px', color: '#64748b', fontWeight: '500' }
};

export default ResultsPage;
